// Building blocks for the two dashboards. Markup and class names follow the
// dashboard-single-branch / dashboard-multi-branch mock-ups, prefixed .axd-*
// (src/style/css/dashboard-screen.css) so nothing collides with Bootstrap.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CMP_OPTIONS, PERIODS, deltaPct, fmtPct } from "./dashData";

/* ================= icons ================= */
const Svg = ({ children, size = 13, sw = 1.6, vb = "0 0 16 16" }) => (
  <svg viewBox={vb} width={size} height={size} fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
export const ICONS = {
  clock: (
    <Svg>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4.6V8l2.4 1.6" />
    </Svg>
  ),
  box: (
    <Svg>
      <path d="M2 5.2 8 2.2l6 3v5.6l-6 3-6-3z" />
      <path d="M2 5.2 8 8.2l6-3M8 8.2v5.6" />
    </Svg>
  ),
  truck: (
    <Svg>
      <path d="M1.6 4.4h7.2v6.2H1.6zM8.8 6.6h2.9l2.7 2.4v1.6H8.8z" />
      <circle cx="4.4" cy="12" r="1.3" />
      <circle cx="11.4" cy="12" r="1.3" />
    </Svg>
  ),
  doc: (
    <Svg>
      <path d="M4 1.8h5l3 3v9.4H4z" />
      <path d="M9 1.8v3h3M6 8h4M6 10.6h4" />
    </Svg>
  ),
  card: (
    <Svg>
      <rect x="1.6" y="3.4" width="12.8" height="9.2" rx="1.6" />
      <path d="M1.6 6.6h12.8" />
    </Svg>
  ),
  chart: (
    <Svg>
      <path d="M2 13.4V2.6M2 13.4h12" />
      <path d="M4.8 11V7.4M7.6 11V4.6M10.4 11V8.8M13.2 11V6" />
    </Svg>
  ),
  tag: (
    <Svg>
      <path d="M2.4 2.4h5.2l6 6-5.2 5.2-6-6z" />
      <circle cx="5" cy="5" r="1" />
    </Svg>
  ),
  lock: (
    <Svg>
      <rect x="3.2" y="7" width="9.6" height="6.6" rx="1.4" />
      <path d="M5.4 7V5.2a2.6 2.6 0 0 1 5.2 0V7" />
    </Svg>
  ),
  quote: (
    <Svg>
      <path d="M3 2.4h10v11.2l-2-1.4-1.7 1.4L8 12.2l-1.3 1.4L5 12.2l-2 1.4z" />
      <path d="M5.6 6h4.8M5.6 8.8h3.2" />
    </Svg>
  ),
  po: (
    <Svg>
      <path d="M2.6 2.6h10.8v10.8H2.6z" />
      <path d="M5.4 6.2h5.2M5.4 9.4h3.4" />
    </Svg>
  ),
  warn: (
    <Svg>
      <path d="M8 2.2 14.4 13H1.6z" />
      <path d="M8 6.4v3M8 11.2h.01" />
    </Svg>
  ),
  cash: (
    <Svg>
      <rect x="1.6" y="4" width="12.8" height="8" rx="1.4" />
      <circle cx="8" cy="8" r="1.8" />
    </Svg>
  ),
  server: (
    <Svg size={16}>
      <rect x="2" y="2.4" width="12" height="4.6" rx="1.2" />
      <rect x="2" y="9" width="12" height="4.6" rx="1.2" />
      <path d="M4.6 4.7h.01M4.6 11.3h.01" />
    </Svg>
  ),
  chev: (
    <Svg size={11} sw={1.8} vb="0 0 12 12">
      <path d="M4.5 2.5 8 6l-3.5 3.5" />
    </Svg>
  ),
  arrow: (
    <Svg size={13} sw={1.5} vb="0 0 14 10">
      <path d="M1 5h11M8.5 1.5 12 5l-3.5 3.5" />
    </Svg>
  ),
  close: (
    <Svg size={12} sw={1.8} vb="0 0 14 14">
      <path d="M3 3l8 8M11 3l-8 8" />
    </Svg>
  ),
  refresh: (
    <Svg size={14}>
      <path d="M14 8a6 6 0 1 1-1.8-4.3M14 2v4h-4" />
    </Svg>
  ),
  download: (
    <Svg size={14}>
      <path d="M8 1.8v8m0 0L5.2 7.2M8 9.8l2.8-2.6M2.2 11.4v1.6a1.2 1.2 0 0 0 1.2 1.2h9.2a1.2 1.2 0 0 0 1.2-1.2v-1.6" />
    </Svg>
  ),
  print: (
    <Svg size={14}>
      <path d="M4.4 6V2.2h7.2V6M4.4 12H3a1.2 1.2 0 0 1-1.2-1.2V7.2A1.2 1.2 0 0 1 3 6h10a1.2 1.2 0 0 1 1.2 1.2v3.6A1.2 1.2 0 0 1 13 12h-1.4M4.4 9.8h7.2v4H4.4z" />
    </Svg>
  ),
  caret: (
    <Svg size={10} vb="0 0 12 12">
      <path d="M3 5l3 3 3-3" />
    </Svg>
  ),
};

/* ================= "needs backend" markers ================= */
// A figure the server cannot give yet. Listed in docs/BACKEND-COMPLETE.md (Part 8).
export const Todo = ({ children = "needs backend" }) => (
  <span className="axd-todo" title="The server does not return this figure yet - see docs/BACKEND-COMPLETE.md (Part 8)">
    {children}
  </span>
);

// A whole block waiting on an endpoint - keeps the mock-up's place in the layout.
export const Pending = ({ what, needs, compact }) => (
  <div className={"axd-pending" + (compact ? " compact" : "")}>
    <span className="ic">{ICONS.server}</span>
    <span className="tx">
      <b>{what}</b>
      {needs ? <span>{needs}</span> : null}
    </span>
    <Todo />
  </div>
);

/* ================= small pieces ================= */
export function Delta({ cur, prev, invert, small }) {
  const p = deltaPct(cur, prev);
  if (p === null) return <span className={"axd-delta flat" + (small ? " sm" : "")}>{cur ? "new" : "—"}</span>;
  const flat = Math.abs(p) < 0.5;
  const good = invert ? p < 0 : p > 0;
  return (
    <span className={"axd-delta " + (flat ? "flat" : good ? "up" : "down") + (small ? " sm" : "")}>
      {!flat && (
        <Svg size={small ? 9 : 10} sw={2} vb="0 0 12 12">
          <path d={p > 0 ? "M6 9.5V2.5M3 5.5 6 2.5l3 3" : "M6 2.5v7M3 6.5 6 9.5l3-3"} />
        </Svg>
      )}
      {fmtPct(p)}
    </span>
  );
}

export const Card = ({ className = "", title, note, right, children, bodyClass = "" }) => (
  <div className={"axd-card " + className}>
    <div className="axd-chead">
      <h2>{title}</h2>
      {note ? <span className="note">{note}</span> : null}
      <span className="sp" />
      {right}
    </div>
    <div className={"axd-cbody " + bodyClass}>{children}</div>
  </div>
);

export const MiniBtn = ({ on, onClick, children, title }) => (
  <button type="button" className={"axd-minibtn" + (on ? " on" : "")} onClick={onClick} title={title}>
    {children}
  </button>
);

export const Seg = ({ items, value, onChange, small, disabledIds = [] }) => (
  <div className={"axd-seg" + (small ? " sm" : "")} role="group">
    {items.map((it) => (
      <button
        type="button"
        key={it.id}
        className={value === it.id ? "on" : ""}
        onClick={() => onChange(it.id)}
        disabled={disabledIds.includes(it.id)}
        title={disabledIds.includes(it.id) ? "Needs backend" : undefined}
      >
        {it.label}
      </button>
    ))}
  </div>
);

export const StackBar = ({ parts, thin, width }) => {
  const total = parts.reduce((a, p) => a + p.v, 0);
  return (
    <span className={"axd-stack" + (thin ? " thin" : "")} style={width ? { width } : undefined}>
      {parts.map((p) => (
        <i key={p.n} title={p.n} style={{ width: (total ? (p.v / total) * 100 : 0).toFixed(2) + "%", background: p.c }} />
      ))}
    </span>
  );
};

export const Facts = ({ items, cols }) => (
  <div className="axd-facts" style={cols ? { gridTemplateColumns: `repeat(${cols},1fr)` } : undefined}>
    {items.map((f) => (
      <div className="axd-fact" key={f.l}>
        <div className="l">{f.l}</div>
        <div className={"v " + (f.cls || "")}>{f.todo ? "—" : f.v}</div>
        <div className="s">{f.todo ? <Todo /> : f.s}</div>
      </div>
    ))}
  </div>
);

// simple table - the chart "Table" twins and the drawer body
export const DataTable = ({ cols, rows, max }) => (
  <div className="axd-tview" style={max === "none" ? { maxHeight: "none" } : undefined}>
    <table>
      <thead>
        <tr>
          {cols.map((c) => (
            <th key={c.k} className={c.num ? "num" : undefined}>
              {c.t}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length ? (
          rows.map((r, i) => (
            <tr key={i} className={r.__total ? "total" : undefined}>
              {cols.map((c) => (
                <td key={c.k} className={c.num ? "num" : undefined}>
                  {r[c.k]}
                </td>
              ))}
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={cols.length} className="empty">
              Nothing in this period.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

/* ================= KPI row ================= */
export function Hero({ label, value, prev, cmpName, spark, footLabel, foot, loading }) {
  return (
    <div className="axd-card axd-hero">
      <div>
        <div className="lbl">{label}</div>
        <div className="fig">{loading ? <span className="axd-shimmer" /> : value}</div>
        <div className="row">
          {!loading && prev !== undefined ? <Delta cur={prev.cur} prev={prev.prev} /> : null}
          <span className="cmp">vs {cmpName}</span>
        </div>
      </div>
      {spark}
      <div className="foot">
        <span>
          {footLabel} {foot}
        </span>
        <span>
          <Todo />
        </span>
      </div>
    </div>
  );
}

export const Tile = ({ label, value, delta, sub, spark, todo, needs, onClick }) => (
  <button type="button" className={"axd-tile" + (todo ? " todo" : "")} onClick={todo ? undefined : onClick} disabled={!!todo} title={todo ? needs : undefined}>
    <span className="lbl">{label}</span>
    <span className="val">{todo ? "—" : value}</span>
    <span className="meta">
      {todo ? <Todo /> : delta}
      <span className="sub">{todo ? needs : sub}</span>
    </span>
    {spark}
  </button>
);

/* ================= needs-attention queue ================= */
const SEVNAME = { crit: "Urgent", warnx: "Soon", info: "Watch" };
export const Queue = ({ items, onPick }) => (
  <div className="axd-queue">
    {items.map((x) => (
      <button
        type="button"
        key={x.key}
        className={"axd-qrow " + x.sev + (x.todo ? " todo" : "")}
        onClick={x.todo || !onPick ? undefined : () => onPick(x)}
        disabled={!!x.todo}
      >
        <span className="ic">{ICONS[x.ic]}</span>
        <span className="txt">
          <span className="t">{x.t}</span>
          <span className="s">
            <span className={"axd-sevlbl " + x.sev}>{SEVNAME[x.sev]}</span>
            <em>{x.s}</em>
          </span>
        </span>
        <span className="n">{x.todo ? <Todo /> : x.n}</span>
        <span className="ch">{ICONS.chev}</span>
      </button>
    ))}
  </div>
);

/* ================= filter row ================= */
export function PeriodBar({ periods = PERIODS, period, onPeriod, cmp, onCmp, vat, onVat, vatLabel, onRefresh, onCsv, onPrint }) {
  return (
    <div className="axd-filters">
      <Seg items={periods} value={period} onChange={onPeriod} />
      <div className="axd-cmpwrap">
        <span>vs</span>
        <div className="axd-selwrap">
          <select value={cmp} onChange={(e) => onCmp(e.target.value)} aria-label="Comparison period">
            {CMP_OPTIONS.map((o) => (
              <option key={o.v} value={o.v}>
                {o.t}
              </option>
            ))}
          </select>
          <span className="caret">{ICONS.caret}</span>
        </div>
      </div>
      <label className="axd-switch">
        <input type="checkbox" checked={vat} onChange={(e) => onVat(e.target.checked)} />
        <span className="track" />
        {vatLabel}
      </label>
      <span className="sp" />
      <button type="button" className="axd-btn" onClick={onRefresh}>
        {ICONS.refresh}Refresh
      </button>
      <button type="button" className="axd-btn" onClick={onCsv}>
        {ICONS.download}CSV
      </button>
      <button type="button" className="axd-btn icon" onClick={onPrint} title="Print" aria-label="Print">
        {ICONS.print}
      </button>
    </div>
  );
}

/* ================= drawer ================= */
export function Drawer({ data, onClose, onCsv, primary }) {
  const [shown, setShown] = useState(false);
  const [held, setHeld] = useState(data);
  useEffect(() => {
    if (data) {
      setHeld(data);
      const id = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => cancelAnimationFrame(id);
    }
    setShown(false);
    const t = setTimeout(() => setHeld(null), 220);
    return () => clearTimeout(t);
  }, [data]);
  if (!held) return null;
  return (
    <>
      <div className={"axd-scrim" + (shown ? " show" : "")} onClick={onClose} />
      <aside className={"axd-drawer" + (shown ? " show" : "")} aria-hidden={!shown} aria-label="Drill down">
        <div className="dhead">
          <div className="ic">{ICONS.doc}</div>
          <div className="tt">
            <h3>{held.title}</h3>
            <div className="s">{held.sub}</div>
          </div>
          <button type="button" className="axd-iconbtn" onClick={onClose} aria-label="Close">
            {ICONS.close}
          </button>
        </div>
        <div className="dbody">
          {held.note ? <div className="axd-dnote">{held.note}</div> : null}
          <DataTable cols={held.cols} rows={held.rows} max="none" />
        </div>
        <div className="dfoot">
          <button type="button" className="axd-btn" onClick={() => onCsv(held)}>
            Export list
          </button>
          {primary}
        </div>
      </aside>
    </>
  );
}

/* ================= toast ================= */
export function useToast() {
  const [msg, setMsg] = useState("");
  const t = useRef(null);
  const show = useCallback((m) => {
    setMsg(m);
    clearTimeout(t.current);
    t.current = setTimeout(() => setMsg(""), 2100);
  }, []);
  useEffect(() => () => clearTimeout(t.current), []);
  return [msg, show];
}
export const Toast = ({ msg }) => <div className={"axd-toast" + (msg ? " show" : "")}>{msg}</div>;

/* ================= keyboard ================= */
// T / Y / W / M / Q pick a period, R refreshes, Esc closes the drawer; extra
// keys come in `more`. Ignored while typing and with any modifier held.
export function useDashKeys({ onPeriod, onRefresh, onEscape, more = {} }) {
  const ref = useRef();
  ref.current = { onPeriod, onRefresh, onEscape, more };
  useEffect(() => {
    const map = Object.fromEntries(PERIODS.filter((p) => p.key).map((p) => [p.key, p.id]));
    const onKey = (e) => {
      if (e.key === "Escape") return ref.current.onEscape();
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const tg = e.target;
      if (tg && (/^(INPUT|SELECT|TEXTAREA)$/.test(tg.tagName) || tg.isContentEditable)) return;
      if (document.querySelector(".modal.show")) return;
      const k = String(e.key).toLowerCase();
      if (map[k]) {
        e.preventDefault();
        ref.current.onPeriod(map[k]);
      } else if (k === "r") {
        e.preventDefault();
        ref.current.onRefresh();
      } else if (ref.current.more[k]) {
        e.preventDefault();
        ref.current.more[k]();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
}

/* ================= page shell ================= */
export function DashScreen({ title, sub, updated, loading, error, children, status, keys, drawer, toast }) {
  return (
    <div className="axd-screen">
      <div className="page-wrapper axd-wrapper">
        <div className={"content axd-page" + (loading ? " loading" : "")}>
          <div className="axd-phead">
            <div>
              <h1>{title}</h1>
              <div className="sub">{sub}</div>
            </div>
            <span className="sp" />
            <div className="sub right">
              <span className="live">
                <span className="dot pulse" />
                {loading ? "Loading…" : "Live"}
              </span>
              <span> · {updated}</span>
            </div>
          </div>
          {error ? <div className="axd-error">{error}</div> : null}
          {children}
          <footer className="axd-status">
            {keys.map(([k, t]) => (
              <span key={k}>
                <kbd>{k}</kbd> {t}
              </span>
            ))}
            <span className="sp" />
            <span>{status}</span>
          </footer>
        </div>
      </div>
      {drawer}
      <Toast msg={toast} />
    </div>
  );
}

// "updated just now" -> "updated 3 min ago"
export function useUpdatedLabel(stamp) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((x) => x + 1), 60000);
    return () => clearInterval(id);
  }, []);
  if (!stamp) return "not loaded yet";
  const m = Math.floor((Date.now() - stamp) / 60000);
  return "updated " + (m < 1 ? "just now" : m + " min ago");
}
